import React, { useEffect, useState, useMemo } from 'react';
import { Backdrop } from '@material-ui/core';
import Button from '@mui/material/Button';
import { Calendar } from '@hassanmojab/react-modern-calendar-datepicker';
import moment from 'jalali-moment';
import PropTypes from 'prop-types';
import styles from './styles.module.scss';
import CalendarIcon from '@material-ui/icons/CalendarToday';
import styled from '@material-ui/core/styles/styled';
import Box from '@material-ui/core/Box';
import { IconButton, TextField } from '@mui/material';
import { useTranslation } from '../../../i18n/LanguageProvider';

const StyledBackdrop = styled(Backdrop)({
  zIndex: 99999,
  backgroundColor: 'rgba(0, 0, 0, 0.8)'
});

const StyledBox = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  margin: '0 16px 16px'
});

const parseValueToCalendarValue = (work_start_date) => {
  if (!work_start_date) return null;
  const value = work_start_date.split('/');
  const key = ['year', 'month', 'day'];
  const json = {};
  key.forEach((v, i) => {
    json[v] = Number(value[i]);
  });
  return json;
};

const DatePicker = ({ name, onChange, value, label }) => {
  // persian: jalali calendar, english: gregorian
  const { t, language } = useTranslation();
  const [openCalendar, setOpenCalendar] = useState(false);
  const [calendarValue, setCalendarValue] = useState(null);

  const inputValue = useMemo(
    () =>
      value
        ? moment(value, 'YYYY-MM-DD').locale(language).format('YYYY/MM/DD')
        : null,
    [value, language]
  );

  useEffect(() => {
    if (inputValue) {
      setCalendarValue(parseValueToCalendarValue(inputValue));
    }
  }, [inputValue]);

  const selectDay = () => {
    onChange(
      moment
        .from(
          `${calendarValue.year}/${calendarValue.month}/${calendarValue.day}`,
          language,
          'YYYY/MM/DD'
        )
        .format('YYYY-MM-DD')
    );
    setOpenCalendar(false);
  };

  const calendarButton = () => (
    <StyledBox component="div">
      <Button
        variant="contained"
        style={{
          flex: 1,
          margin: '0 8px'
        }}
        type="button"
        onClick={selectDay}
      >
        {t('common.submit')}
      </Button>
      <Button
        style={{
          flex: 1,
          margin: '0 8px'
        }}
        variant="outlined"
        type="button"
        onClick={() => {
          setOpenCalendar(false);
        }}
      >
        {t('common.cancel')}
      </Button>
    </StyledBox>
  );

  return (
    <>
      <TextField
        className={styles.input}
        label={label}
        name={name}
        value={inputValue}
        InputProps={{
          endAdornment: (
            <IconButton
              onClick={() => {
                setOpenCalendar(true);
              }}
            >
              <CalendarIcon />
            </IconButton>
          ),
          disableUnderline: true
        }}
      />
      <StyledBackdrop mountOnEnter unmountOnExit open={openCalendar}>
        <div onClick={(e) => e.stopPropagation()}>
          <Calendar
            value={calendarValue ?? parseValueToCalendarValue(inputValue)}
            onChange={setCalendarValue}
            shouldHighlightWeekends
            locale={language}
            renderFooter={calendarButton}
          />
        </div>
      </StyledBackdrop>
    </>
  );
};

DatePicker.propTypes = {
  name: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  /**
   * string format YYYY-MM-DD
   */
  value: PropTypes.string,
  label: PropTypes.string
};

export default DatePicker;
